import { useState } from "react";
import { motion } from "framer-motion";
import { Car, CheckCircle2, ChevronDown, Clock, MapPin, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { mapEmbedSrc, directionsUrl, fullAddress, rhythmAccentStyle, ordinalWord, locTimes, timeRange, clockWord, DEFAULT_START } from "@/lib/fridays";
import { shortDate } from "@/lib/nextFriday";

export function venueSlug(loc) {
  return (loc?.name || "venue").toLowerCase().replace(/[—–-].*$/, "").trim().split(/\s+/).slice(0, 2).join("-").replace(/[^a-z0-9-]/g, "");
}

export default function VenueCard({ loc, nextDate, index = 0 }) {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const isNext = !!nextDate;
  const prefix = `landing-location-${loc.friday_ordinal}`;
  const steps = Array.isArray(loc.directions) ? loc.directions : [];
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1], delay: index * 0.06 }}
      style={rhythmAccentStyle(loc.friday_ordinal)}
      className="h-full"
    >
      <article
        className={`relative flex h-full flex-col overflow-hidden rounded-3xl border bg-card transition-shadow duration-300 hover:shadow-[0_24px_60px_rgba(92,52,18,0.12)] ${isNext ? "border-transparent ring-2 ring-primary shadow-[0_24px_60px_rgba(201,119,46,0.18)]" : "border-border"}`}
        data-testid={`venue-card-${venueSlug(loc)}`}
        data-next={isNext ? "1" : "0"}
      >
        {isNext && (
          <div className="flex items-center justify-between gap-2 bg-primary px-5 py-2 text-xs font-semibold uppercase tracking-widest text-primary-foreground" data-testid="venue-next-ribbon">
            <span>Next up</span><span>{shortDate(nextDate)} · {clockWord(locTimes(loc).start)}</span>
          </div>
        )}
        {loc.postcard_image && (
          <div className="relative aspect-[3/2] overflow-hidden bg-secondary" data-testid={`${prefix}-postcard`}>
            <img src={loc.postcard_image} alt={`Illustration of ${loc.name}`} loading="lazy" className="h-full w-full object-cover" />
            <span
              className="pointer-events-none absolute left-4 top-4 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest text-white shadow"
              style={{ backgroundColor: "var(--fc-friday-accent)" }}
            >
              {ordinalWord(loc.friday_ordinal)} Friday
            </span>
            <span className="pointer-events-none absolute right-4 top-4 rotate-6 rounded-sm border-2 border-dashed border-white/85 bg-black/15 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-white backdrop-blur-sm">Spokane · Fri</span>
          </div>
        )}
        <div className={`relative bg-secondary ${loc.postcard_image ? "h-40 border-t border-border sm:h-44" : "h-52 sm:h-56"}`}>
          {!mapLoaded && <Skeleton className="absolute inset-0 h-full w-full rounded-none" />}
          <iframe
            title={`Map of ${loc.name}`}
            src={mapEmbedSrc(loc)}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            onLoad={() => setMapLoaded(true)}
            className={`h-full w-full border-0 transition-opacity duration-500 ${mapLoaded ? "opacity-100" : "opacity-0"}`}
            data-testid={`${prefix}-map-iframe`}
          />
          {!loc.postcard_image && (
            <span
              className="pointer-events-none absolute left-4 top-4 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest text-white shadow"
              style={{ backgroundColor: "var(--fc-friday-accent)" }}
            >
              {ordinalWord(loc.friday_ordinal)} Friday
            </span>
          )}
        </div>

        <div className="flex flex-1 flex-col p-5 sm:p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: "var(--fc-friday-accent)" }}>{loc.theme_title}</p>
          <h3 className="mt-1.5 font-display text-2xl font-semibold leading-tight text-foreground">{loc.name}</h3>
          {loc.room && <p className="mt-1 text-sm font-medium text-foreground/80">{loc.room}</p>}
          <p className="mt-3 flex items-start gap-2 text-sm text-muted-foreground" data-testid={`${prefix}-address`}>
            <MapPin className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "var(--fc-friday-accent)" }} />
            <span>{fullAddress(loc)}</span>
          </p>
          <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground" data-testid={`${prefix}-hours`}>
            <Clock className="h-4 w-4 shrink-0" style={{ color: "var(--fc-friday-accent)" }} />
            <span>{ordinalWord(loc.friday_ordinal)} Fridays · <span className="font-semibold text-foreground">{timeRange(locTimes(loc).start, locTimes(loc).end)}</span> Pacific</span>
            {locTimes(loc).start !== DEFAULT_START && <span className="inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold text-white" style={{ backgroundColor: "var(--fc-friday-accent)" }}>later start</span>}
          </p>

          {steps.length > 0 && (
            <div className="mt-4">
              <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between rounded-xl border border-border bg-background px-3.5 py-2.5 text-left text-sm font-semibold text-foreground transition-colors hover:bg-secondary sm:cursor-default sm:pointer-events-none" aria-expanded={open} data-testid={`${prefix}-directions-toggle`}>
                How to find the room
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform sm:hidden ${open ? "rotate-180" : ""}`} />
              </button>
              <ul className={`mt-2 space-y-2 text-sm text-muted-foreground ${open ? "block" : "hidden sm:block"}`} data-testid={`${prefix}-directions`}>
                {steps.map((d, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "var(--fc-friday-accent)" }} />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {loc.parking && (
            <p className="mt-3 flex items-start gap-2.5 text-sm text-muted-foreground" data-testid={`${prefix}-parking-note`}>
              <Car className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <span><span className="font-medium text-foreground">Parking:</span> {loc.parking}</span>
            </p>
          )}

          <div className="mt-auto pt-5">
            <Button asChild className="w-full rounded-full bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))] sm:w-auto" data-testid={`${prefix}-get-directions-button`}>
              <a href={directionsUrl(loc)} target="_blank" rel="noopener noreferrer">
                <Navigation className="mr-1.5 h-4 w-4" /> Get directions
              </a>
            </Button>
          </div>
        </div>
      </article>
    </motion.div>
  );
}
